// Resolves a vendor's *effective* question set. A questionnaire's membership
// AND per-question config are frozen by a snapshot written when it is sent /
// the vendor is invited (and at first submit as a backstop), so questions
// added later never appear and NO later master edit reaches an
// already-invited vendor. The freeze is absolute: the only field still read
// from the live row is `deleted_at`, the retirement kill switch. Questionnaires
// with no snapshot show the current live set. Pure + structural so the API
// Worker and the admin web app share one rule. progress.ts handles how
// soft-deleted questions are excluded from gating once they are in the set.
// See docs/superpowers/specs/2026-08-18-absolute-questionnaire-freeze-design.md.

export type SnapshotRow = {
  questionnaire_id: string;
  question_id: string;
  /** The whole frozen question row + its adoption_triggers, captured at
   *  snapshot time (migration 0061; shape mirrors select('*,
   *  adoption_triggers(*)')). Null/absent = legacy pre-0061 row → fall back
   *  to the live question. */
  config?: unknown;
};

type EffectiveQuestion = {
  id: string;
  questionnaire_id: string | null;
  deleted_at?: string | null;
};

/**
 * For each question: if its questionnaire is in `frozenQuestionnaireIds`, keep
 * the question only when it is itself in the snapshot — so post-snapshot
 * additions drop out and snapshotted-but-retired questions stay in — and return
 * it hydrated from the row's frozen config blob (the live row is used only when
 * config is null, for legacy rows). The frozen set is passed in rather than
 * inferred from the snapshot rows: a questionnaire frozen while it had no
 * questions has no rows, and inferring would serve every later addition live.
 * The live `deleted_at` is always overlaid onto the hydrated
 * question so a soft-delete AFTER the snapshot still removes it from gating
 * (progress.ts skips deleted_at) — otherwise retiring a question would re-arm
 * it for every already-invited vendor. For an un-frozen questionnaire, keep
 * the live *active* set (deleted_at null), unchanged.
 * Output preserves the input array's order (the callers' live display_order
 * sort).
 */
export function selectEffectiveQuestions<Q extends EffectiveQuestion>(
  questions: Q[],
  snapshot: SnapshotRow[],
  frozenQuestionnaireIds: ReadonlySet<string>,
): Q[] {
  // Key by questionnaire_id + question_id so a question is matched only against
  // its OWN questionnaire's snapshot. A flat map of question ids would wrongly
  // retain (or worse, hydrate) a question from another questionnaire's snapshot
  // that shared its id. The null-byte separator can never appear in an id, so
  // keys cannot collide.
  const configByKey = new Map<string, unknown>(
    snapshot.map((r) => [`${r.questionnaire_id}\0${r.question_id}`, r.config ?? null]),
  );
  const result: Q[] = [];
  for (const q of questions) {
    if (q.questionnaire_id && frozenQuestionnaireIds.has(q.questionnaire_id)) {
      const key = `${q.questionnaire_id}\0${q.id}`;
      if (!configByKey.has(key)) continue; // added after the snapshot: drop
      const config = configByKey.get(key);
      if (config == null) {
        result.push(q); // legacy pre-0061 row: no frozen config → live
      } else {
        // Freeze the captured config wholesale, keeping only the live
        // retirement marker so a question soft-deleted after the snapshot
        // stops gating. Spread the live row first so a question column added
        // after this blob was captured falls back to live rather than being
        // undefined.
        result.push({ ...q, ...(config as Q), deleted_at: q.deleted_at ?? null });
      }
    } else if (q.deleted_at == null) {
      result.push(q);
    }
  }
  return result;
}
